import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { activitiesTabs, activitiesContent } from '@/mocks/activitiesData';
import StoreChoiceModal from '@/components/feature/StoreChoiceModal';

export default function ActivitiesSection() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('adults');
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const lang = i18n.language as 'fr' | 'en';
  const content = activitiesContent[activeTab as keyof typeof activitiesContent];

  const renderItemList = (items: string[]) => (
    <ul className="space-y-3">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-fill text-accent-500 text-sm"></i>
          </div>
          <span className="text-sm md:text-base text-foreground-700">{t(item)}</span>
        </li>
      ))}
    </ul>
  );

  const renderCampContent = () => {
    const campData = content as typeof activitiesContent.camp;
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row gap-3">
          {campData.sessions.map((session, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-primary-500 text-background-50 px-4 py-2 rounded-full text-sm font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-line text-xs"></i>
              </div>
              <span>{t(session)}</span>
            </div>
          ))}
        </div>
        <div className="bg-accent-50/50 border border-accent-200/50 rounded-lg px-4 py-3 inline-block">
          <span className="text-sm font-medium text-accent-800">{t('activities.camp.schedule')}</span>
        </div>
        {renderItemList(campData.activities)}
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'camp') return renderCampContent();
    const data = content as { items: string[] };
    return renderItemList(data.items);
  };

  return (
    <section id="activities" className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent-600">{t('activities.title')}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2">{t('activities.subtitle')}</h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {activitiesTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-600 hover:text-primary-600 hover:bg-primary-50 border border-foreground-200'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-xs`}></i>
              </div>
              {t(tab.key)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-14">
          <div className="w-full lg:w-1/2">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={(content as { image: string }).image}
                alt={t(`activities.${activeTab}.title`) || t('activities.title')}
                className={`w-full h-[300px] md:h-[400px] object-cover ${activeTab === 'rental' ? 'object-bottom' : 'object-top'}`}
              />
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              {t(`activities.${activeTab}.title`)}
            </h3>
            <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed">
              {t(`activities.${activeTab}.desc`)}
            </p>
            {renderContent()}
            {(activeTab === 'camp' || activeTab === 'coaching' || activeTab === 'itf' || activeTab === 'rental') && (
              activeTab === 'rental' ? (
                <button
                  onClick={() => setStoreModalOpen(true)}
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t(`activities.${activeTab}.cta`)}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </button>
              ) : (
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t(`activities.${activeTab}.cta`)}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              )
            )}
          </div>
        </div>
      </div>

      <StoreChoiceModal open={storeModalOpen} onClose={() => setStoreModalOpen(false)} />
    </section>
  );
}